/**
 * Birth-month age screen helpers (spec: docs/superpowers/specs/2026-10-14-birth-month-age-screen-design.md).
 * The form asks month + year only, shows no age cutoff, sends `birth_month` and lets the server decide.
 * After a refusal the form is held for 24 hours on this device (the FTC's "no back-button retry" cookie).
 */
export const SIGNUP_HOLD_KEY = 'signup_hold_until';
export const SIGNUP_HOLD_MS = 24 * 60 * 60 * 1000;
export const NEUTRAL_REFUSAL = "We can't create an account with those details.";
export const BIRTH_MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

/** Current year back 100 years, newest first. */
export function birthYears(now: Date = new Date()): string[] {
  const y = now.getFullYear();
  return Array.from({ length: 100 }, (_, i) => String(y - i));
}

/** `YYYY-MM` for a complete pair, else null. No age arithmetic here: the server owns the rule. */
export function composeBirthMonth(year: string, month: string): string | null {
  if (!/^\d{4}$/.test(year) || !/^(0[1-9]|1[0-2])$/.test(month)) return null;
  return `${year}-${month}`;
}

/**
 * True when an API error message is the server's age refusal: signup answers 400 with an
 * `age_too_young:` prefix, onboarding answers 403 (reason `age_ineligible`) with the bare neutral sentence.
 */
export function isAgeRefusal(message: string): boolean {
  return message.startsWith('age_too_young:') || message === NEUTRAL_REFUSAL;
}

export function startSignupHold(now: number = Date.now(), storage: Storage = localStorage): void {
  try {
    storage.setItem(SIGNUP_HOLD_KEY, String(now + SIGNUP_HOLD_MS));
  } catch {
    /* storage blocked: the server still refuses */
  }
}

export function signupHoldActive(now: number = Date.now(), storage: Storage = localStorage): boolean {
  try {
    const until = Number(storage.getItem(SIGNUP_HOLD_KEY) ?? 0);
    // A deadline further ahead than one hold is clock skew or tampering: treat it as inactive.
    return until > now && until <= now + SIGNUP_HOLD_MS;
  } catch {
    return false;
  }
}
